// Shared by the homepage drawer and the product page. Keep the module lazy.
window.mtlLoadBoxCatalog = window.mtlLoadBoxCatalog || (() => {
  const script = document.currentScript || document.querySelector('script[src*="/monteleone.js"]');
  const url = new URL('monteleone-box-catalog.js', script.src);
  url.search = new URL(script.src).search;
  let promise;
  return () => (promise ||= import(url.href));
})();

class MonteleoneBoxGrid extends HTMLElement {
  connectedCallback() {
    this.filters = this.querySelectorAll('[data-mtl-filter]');
    this.cards = this.querySelectorAll('[data-mtl-category]');
    this.overlay = this.querySelector('[data-mtl-overlay]');
    this.form = this.querySelector('[data-mtl-config-form]');
    this.drawerTitle = this.querySelector('[data-mtl-drawer-title]');
    this.total = this.querySelector('[data-mtl-total]');
    this.delta = this.querySelector('[data-mtl-delta]');
    this.fixedList = this.querySelector('[data-mtl-fixed-list]');
    this.error = this.querySelector('[data-mtl-error]');
    this.addButton = this.querySelector('[data-mtl-add]');
    this.toast = this.querySelector('[data-mtl-toast]');
    this.currency = this.dataset.mtlCurrency || 'EUR';

    this.activeConfig = null;
    this.selectedOptions = [];
    this.lastTrigger = null;
    this.lightboxLastTrigger = null;
    this.toastTimer = null;

    this.createPosterLightbox();
    this.preparePosterZoom();

    this.filters.forEach((filter) => {
      filter.addEventListener('click', () => this.applyFilter(filter));
    });

    this.querySelectorAll('[data-mtl-open-configurator]').forEach((button) => {
      button.addEventListener('click', () => this.openConfigurator(button));
    });

    this.querySelector('[data-mtl-close]')?.addEventListener('click', () => {
      this.closeConfigurator();
    });

    this.querySelector('[data-mtl-reset]')?.addEventListener('click', () => {
      this.resetConfigurator();
    });

    this.addButton?.addEventListener('click', () => {
      this.addSelectedVariant();
    });

    this.form?.addEventListener('change', (event) => {
      this.handleOptionChange(event);
    });

    this.overlay?.addEventListener('click', (event) => {
      if (event.target === this.overlay) {
        this.closeConfigurator();
      }
    });

    this.handleGlobalKeydownBound = (event) => {
      this.handleGlobalKeydown(event);
    };

    document.addEventListener('keydown', this.handleGlobalKeydownBound);
  }

  disconnectedCallback() {
    this.variantRequest?.abort();
    window.clearTimeout(this.toastTimer);
    if (this.handleGlobalKeydownBound) {
      document.removeEventListener('keydown', this.handleGlobalKeydownBound);
    }
  }

  createPosterLightbox() {
    let lightbox = document.querySelector('[data-mtl-global-poster-lightbox]');

    if (!lightbox) {
      lightbox = document.createElement('div');

      lightbox.className = 'mtl-poster-lightbox';
      lightbox.dataset.mtlGlobalPosterLightbox = '';
      lightbox.setAttribute('aria-hidden', 'true');

      lightbox.innerHTML = `
        <div
          class="mtl-poster-lightbox__dialog"
          role="dialog"
          aria-modal="true"
          aria-label="Locandina cocktail"
        >
          <button
            class="mtl-poster-lightbox__close"
            type="button"
            data-mtl-poster-close
            aria-label="Chiudi locandina"
          >
            ×
          </button>

          <div class="mtl-poster-lightbox__stage">
            <img
              class="mtl-poster-lightbox__image"
              data-mtl-poster-image
              src=""
              alt=""
            >
          </div>

          <p
            class="mtl-poster-lightbox__caption"
            data-mtl-poster-caption
          ></p>
        </div>
      `;

      document.body.appendChild(lightbox);
    }

    this.posterLightbox = lightbox;
    this.posterLightboxImage = lightbox.querySelector('[data-mtl-poster-image]');
    this.posterLightboxCaption = lightbox.querySelector('[data-mtl-poster-caption]');
    this.posterLightboxClose = lightbox.querySelector('[data-mtl-poster-close]');

    if (!lightbox.dataset.mtlEventsReady) {
      lightbox.dataset.mtlEventsReady = 'true';

      this.posterLightboxClose?.addEventListener('click', () => {
        this.closePosterLightbox();
      });

      lightbox.addEventListener('click', (event) => {
        if (event.target === lightbox) {
          this.closePosterLightbox();
        }
      });
    }
  }

  preparePosterZoom() {
    this.querySelectorAll('.mtl-box-art').forEach((poster) => {
      const image = poster.querySelector('img');

      if (!image || poster.hasAttribute('data-mtl-package-art')) return;

      poster.classList.add('is-zoomable');
      poster.setAttribute('role', 'button');
      poster.setAttribute('tabindex', '0');
      poster.setAttribute('aria-label', 'Apri la locandina e leggi la ricetta');

      poster.addEventListener('click', () => {
        this.openPosterLightbox(poster);
      });

      poster.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          this.openPosterLightbox(poster);
        }
      });
    });
  }

  openPosterLightbox(poster) {
    const image = poster.querySelector('img');

    if (!image || !this.posterLightbox) return;

    const card = poster.closest('.mtl-box-card');

    const ritual =
      card?.querySelector('.mtl-box-ritual')?.textContent?.trim() || '';

    const title =
      card?.querySelector('.mtl-box-body h3')?.textContent?.trim() ||
      ritual ||
      'Locandina cocktail';

    this.lightboxLastTrigger = poster;

    this.posterLightboxImage.src = image.currentSrc || image.src;
    this.posterLightboxImage.alt = image.alt || title;
    this.posterLightboxCaption.textContent = title;

    this.posterLightbox.classList.add('is-open');
    this.posterLightbox.setAttribute('aria-hidden', 'false');

    document.documentElement.classList.add('mtl-poster-open');
    document.body.classList.add('mtl-poster-open');

    requestAnimationFrame(() => {
      this.posterLightboxClose?.focus();
    });
  }

  closePosterLightbox() {
    if (!this.posterLightbox?.classList.contains('is-open')) return;

    this.posterLightbox.classList.remove('is-open');
    this.posterLightbox.setAttribute('aria-hidden', 'true');

    document.documentElement.classList.remove('mtl-poster-open');
    document.body.classList.remove('mtl-poster-open');

    window.setTimeout(() => {
      if (this.posterLightboxImage) {
        this.posterLightboxImage.src = '';
      }
    }, 250);

    this.lightboxLastTrigger?.focus();
  }

  applyFilter(activeFilter) {
    const value = activeFilter.dataset.mtlFilter;

    this.filters.forEach((filter) => {
      const active = filter === activeFilter;

      filter.classList.toggle('is-active', active);
      filter.setAttribute('aria-pressed', String(active));
    });

    this.cards.forEach((card) => {
      card.hidden =
        value !== 'all' &&
        card.dataset.mtlCategory !== value;
    });
  }

  async openConfigurator(trigger) {
    if (this.adding) return;
    const openSequence = this.openSequence = (this.openSequence || 0) + 1;
    const card = trigger.closest('.mtl-box-card');
    const dataElement = card?.querySelector('[data-mtl-box-config]');

    if (!dataElement) return;

    try {
      this.activeConfig = JSON.parse(dataElement.textContent);
    } catch (error) {
      this.showError('Non è stato possibile caricare il configuratore.');
      return;
    }

    this.boxSpec = null;
    trigger.disabled = true;
    try {
      this.catalog = await window.mtlLoadBoxCatalog();
      if (openSequence !== this.openSequence || !this.isConnected) return;
      this.boxSpec = this.catalog.getSpec(this.activeConfig.product.handle);
      if (this.boxSpec) this.activeConfig.product.options = this.catalog.groups(this.boxSpec).map(group => group.type);
    } catch (error) {
      // Keep the native, previously available combinations as a safe fallback.
      this.boxSpec = null;
    } finally {
      trigger.disabled = false;
    }

    const product = this.activeConfig.product;
    const recommended = this.getRecommendedVariant();

    if (!product || !recommended) {
      this.showError(
        'Questo prodotto non dispone ancora di una combinazione acquistabile.'
      );
      return;
    }

    this.lastTrigger = trigger;
    this.selectedOptions = [...recommended.options];

    this.drawerTitle.textContent = product.title;

    this.renderOptions();
    this.renderFixedItems();
    this.hideError();
    this.updateDrawer();

    this.overlay.classList.add('is-open');
    this.overlay.setAttribute('aria-hidden', 'false');

    document.documentElement.classList.add('mtl-configurator-open');

    requestAnimationFrame(() => {
      this.querySelector('[data-mtl-close]')?.focus();
    });
  }

  closeConfigurator() {
    if (!this.overlay?.classList.contains('is-open')) return;
    this.variantRequest?.abort();
    this.openSequence = (this.openSequence || 0) + 1;
    this.total?.removeAttribute('aria-busy');

    this.overlay.classList.remove('is-open');
    this.overlay.setAttribute('aria-hidden', 'true');

    document.documentElement.classList.remove('mtl-configurator-open');

    this.lastTrigger?.focus();
  }

  renderOptions() {
    const product = this.activeConfig.product;
    const recommended = this.getRecommendedVariant();

    this.form.innerHTML = '';

    product.options.forEach((optionName, optionIndex) => {
      const fieldset = document.createElement('fieldset');

      fieldset.className = 'mtl-configurator__group';

      const legend = document.createElement('legend');

      legend.textContent = optionName;

      fieldset.appendChild(legend);

      const note = document.createElement('span');

      note.className = 'mtl-configurator__note';
      note.textContent = this.optionNote(optionName);

      fieldset.appendChild(note);

      const grid = document.createElement('div');

      grid.className = 'mtl-configurator__options';

      const values = this.boxSpec ? this.catalog.groups(this.boxSpec)[optionIndex].values.map(value => value.name) : [
        ...new Set(
          product.variants.map(
            (variant) => variant.options[optionIndex]
          )
        )
      ];

      values.forEach((value, valueIndex) => {
        const option = document.createElement('div');

        option.className = 'mtl-configurator__option';

        const id =
          `MtlOption-${product.id}-${optionIndex}-${valueIndex}`;

        const input = document.createElement('input');

        input.type = 'radio';
        input.id = id;
        input.name = `mtl-option-${product.id}-${optionIndex}`;
        input.value = value;
        input.dataset.optionIndex = String(optionIndex);
        input.checked = value === this.selectedOptions[optionIndex];

        const label = document.createElement('label');

        label.htmlFor = id;

        const title = document.createElement('span');

        title.className = 'mtl-configurator__option-title';
        title.textContent = value;

        if (value === recommended.options[optionIndex]) {
          const badge = document.createElement('span');

          badge.className = 'mtl-configurator__recommended';
          badge.textContent = 'Standard';

          title.appendChild(badge);
        }

        const difference = document.createElement('span');

        difference.className = 'mtl-configurator__option-diff';
        difference.dataset.mtlOptionDifference = '';

        const imageUrl = this.optionImage(value);
        if (imageUrl) {
          const photo = document.createElement('img');
          photo.src = imageUrl;
          photo.alt = imageUrl.includes('monteleone-box-closed') ? 'Confezione Monteleone' : value;
          photo.width = 40;
          photo.height = 58;
          photo.loading = 'lazy';
          photo.style.cssText = 'width:40px;height:58px;object-fit:contain;flex:none';
          label.appendChild(photo);
        }
        title.style.flex = '1';
        label.append(title, difference);
        option.append(input, label);
        grid.appendChild(option);
      });

      fieldset.appendChild(grid);
      this.form.appendChild(fieldset);
    });

    // These bottles are already part of the box, even though Shopify has no
    // option axis when there is only one choice. Show them without changing price.
    const singleChoices = this.getSingleChoices();
    singleChoices.forEach(({ name, value }) => {
      const group = document.createElement('fieldset');
      group.className = 'mtl-configurator__group';
      const legend = document.createElement('legend');
      legend.textContent = name;
      const card = document.createElement('div');
      card.className = 'mtl-single-choice';
      const imageUrl = this.activeConfig.optionImages?.[value];
      if (imageUrl) {
        const photo = document.createElement('img');
        photo.src = imageUrl; photo.alt = value;
        photo.width = 40; photo.height = 58; photo.loading = 'lazy';
        card.appendChild(photo);
      }
      const title = document.createElement('strong');
      title.textContent = value;
      const badge = document.createElement('span');
      badge.className = 'mtl-configurator__recommended';
      badge.textContent = 'Standard';
      title.appendChild(badge);
      const price = document.createElement('span');
      price.textContent = '+0,00 €';
      const note = document.createElement('small');
      note.textContent = 'Già presente nella composizione standard · unica opzione';
      card.append(title, price);
      group.append(legend, card, note);
      this.form.appendChild(group);
    });
  }

  getSingleChoices() {
    const handle = this.activeConfig?.product?.handle;
    if (handle === 'rum-passion-mocktail-box') return [{ name: 'Rum zero alcol', value: 'RHHM' }];
    if (handle === 'larte-del-bitter-mocktail-box') return [
      { name: 'Bitter zero alcol', value: 'BTTR' },
      { name: 'Vermouth zero alcol', value: 'VRMH' }
    ];
    return [];
  }

  optionImage(value) {
    const images = this.activeConfig.optionImages || {};
    if (images[value]) return images[value];
    if (!this.catalog) return null;
    const key = Object.keys(images).find(name => this.catalog.normalize(name) === this.catalog.normalize(value));
    return key ? images[key] : null;
  }

  renderFixedItems() {
    this.fixedList.innerHTML = '';

    (this.activeConfig.fixed || [])
      .filter(Boolean)
      .forEach((item) => {
        if (this.boxSpec && this.catalog.groups(this.boxSpec).some(group => group.type === 'Soda')) {
          item = item.replace(/Soda (?:Thomas Henry|Fever Tree|premium)(?: e |, )?/gi, '').trim();
          if (!item) return;
        }
        if (this.getSingleChoices().length) {
          item = item.replace(/RHHM, alternativa analcolica al rum|BTTR, VRMH e /g, '').trim();
          if (!item) return;
        }
        const li = document.createElement('li');

        li.textContent = item;

        this.fixedList.appendChild(li);
      });
  }

  handleOptionChange(event) {
    const input = event.target.closest('input[data-option-index]');

    if (!input) return;

    this.selectedOptions[
      Number(input.dataset.optionIndex)
    ] = input.value;

    this.hideError();
    this.updateDrawer();
  }

  async updateDrawer() {
    if (this.adding) return;
    this.variantRequest?.abort();
    const request = this.variantRequest = new AbortController();
    const selection = [...this.selectedOptions];
    this.resolvedVariant = null;
    const recommended = this.getRecommendedVariant();
    const selected = this.findVariant(this.selectedOptions);

    this.form
      .querySelectorAll('input[data-option-index]')
      .forEach((input) => {
        const optionIndex = Number(input.dataset.optionIndex);

        const candidateOptions = [...this.selectedOptions];

        candidateOptions[optionIndex] = input.value;

        const candidate = this.findVariant(candidateOptions);

        input.disabled =
          !candidate ||
          !candidate.available;

        input
          .closest('.mtl-configurator__option')
          ?.classList.toggle(
            'is-unavailable',
            input.disabled
          );

        const referenceOptions = [...recommended.options];

        referenceOptions[optionIndex] = input.value;

        const referenceVariant =
          this.findVariant(referenceOptions);

        const difference =
          referenceVariant
            ? referenceVariant.price - recommended.price
            : 0;

        const differenceElement =
          input.nextElementSibling?.querySelector(
            '[data-mtl-option-difference]'
          );

        if (differenceElement) {
          differenceElement.textContent =
            this.formatDifference(difference);
        }
      });

    if (!selected) {
      this.total.textContent = '—';
      this.delta.textContent = 'Combinazione non disponibile';
      this.addButton.disabled = true;
      return;
    }

    const difference =
      selected.price - recommended.price;

    this.total.textContent =
      this.formatMoney(selected.price);

    this.delta.textContent =
      difference === 0
        ? 'Composizione standard'
        : `${difference > 0 ? '+' : '−'}${this.formatMoney(
            Math.abs(difference)
          )} rispetto alla base`;

    this.addButton.disabled = !selected.available;
    if (this.boxSpec) {
      this.addButton.disabled = true;
      this.total.setAttribute('aria-busy', 'true');
      try {
        const variant = await this.catalog.resolve(this.boxSpec, selection, {signal:request.signal});
        if (request.signal.aborted || !this.isConnected) return;
        this.resolvedVariant = variant;
        this.total.textContent = this.formatMoney(variant.price);
        this.addButton.disabled = this.adding || !variant.available;
        if (!variant.available) this.showError('La combinazione selezionata non è disponibile.');
      } catch (error) {
        if (!request.signal.aborted) this.showError(error.message);
      } finally {
        if (!request.signal.aborted) this.total.removeAttribute('aria-busy');
      }
    }
  }

  resetConfigurator() {
    if (this.adding) return;
    const recommended = this.getRecommendedVariant();

    if (!recommended) return;

    this.selectedOptions = [...recommended.options];

    this.form
      .querySelectorAll('input[data-option-index]')
      .forEach((input) => {
        input.checked =
          input.value ===
          this.selectedOptions[
            Number(input.dataset.optionIndex)
          ];
      });

    this.hideError();
    this.updateDrawer();
  }

  async addSelectedVariant() {
    if (this.adding) return;
    const selection = [...this.selectedOptions];
    let variant =
      this.findVariant(selection);

    if (!variant?.available) {
      this.showError(
        'La combinazione selezionata non è disponibile.'
      );
      return;
    }

    const originalText = this.addButton.innerHTML;

    this.adding = true;
    this.variantRequest?.abort();
    this.form.querySelectorAll('input[data-option-index]').forEach(input => { input.disabled = true; });
    this.addButton.disabled = true;
    this.addButton.textContent = 'Aggiunta in corso…';

    this.hideError();

    try {
      if (this.boxSpec) {
        // Re-read price and availability immediately before the cart request.
        variant = await this.catalog.resolve(this.boxSpec, selection);
        if (!variant.available) throw new Error('La combinazione selezionata non è disponibile.');
      }
      if (window.Shopify?.actions?.updateCart) {
        const result =
          await window.Shopify.actions.updateCart(
            {
              lines: [
                {
                  merchandiseId: String(variant.id),
                  quantity: 1
                }
              ]
            },
            {
              event: {
                context: 'dialog',
                detail: {
                  source: 'monteleone-configurator'
                }
              }
            }
          );

        if (result.userErrors?.length) {
          throw new Error(result.userErrors[0].message);
        }
      } else {
        const response = await fetch(
          `${window.Shopify?.routes?.root || '/'}cart/add.js`,
          {
            method: 'POST',

            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json'
            },

            body: JSON.stringify({
              items: [
                {
                  id: variant.id,
                  quantity: 1
                }
              ]
            })
          }
        );

        const result = await response.json();

        if (!response.ok) {
          throw new Error(
            result.description ||
            'Impossibile aggiungere la box al carrello.'
          );
        }
      }

      this.closeConfigurator();

      this.showToast(
        this.dataset.mtlSuccess ||
        'Box aggiunta al carrello.'
      );

    } catch (error) {
      this.showError(
        error.message ||
        'Impossibile aggiungere la box al carrello.'
      );

    } finally {
      this.adding = false;
      this.addButton.innerHTML = originalText;
      this.addButton.disabled = false;

      this.updateDrawer();
    }
  }

  getRecommendedVariant() {
    if (this.boxSpec) return this.catalog.quote(this.boxSpec, this.catalog.standardOptions(this.boxSpec));
    const variants =
      this.activeConfig?.product?.variants || [];

    return (
      variants.find(
        (variant) =>
          String(variant.id) ===
          String(this.activeConfig.recommendedVariantId)
      ) ||
      variants.find(
        (variant) => variant.available
      ) ||
      variants[0]
    );
  }

  findVariant(options) {
    if (this.boxSpec) return this.catalog.quote(this.boxSpec, options);
    return this.activeConfig.product.variants.find(
      (variant) =>
        variant.options.every(
          (value, index) =>
            value === options[index]
        )
    );
  }

  optionNote(name) {
    const key =
      name.toLocaleLowerCase('it-IT');

    if (key.includes('gin')) {
      return 'Scegli il profilo aromatico del tuo cocktail';
    }

    if (key.includes('bitter')) {
      return 'Scegli il profilo amaro';
    }

    if (key.includes('vermouth')) {
      return 'Completa l’equilibrio dolce-speziato';
    }

    if (key.includes('rum')) {
      return 'Dal profilo morbido a quello più intenso';
    }

    if (
      key.includes('tequila') ||
      key.includes('agave')
    ) {
      return 'Scegli il carattere del tuo rituale';
    }

    if (key.includes('spirit')) {
      return 'Scegli il profilo botanico';
    }

    return 'Scegli soltanto se vuoi cambiare la selezione standard';
  }

  formatMoney(cents) {
    return new Intl.NumberFormat(
      'it-IT',
      {
        style: 'currency',
        currency: this.currency,
        minimumFractionDigits:
          cents % 100 === 0 ? 0 : 2,
        maximumFractionDigits: 2
      }
    ).format(cents / 100);
  }

  formatDifference(cents) {
    if (cents === 0) {
      return '+0,00 €';
    }

    return `${cents > 0 ? '+' : '−'}${this.formatMoney(
      Math.abs(cents)
    )}`;
  }

  showError(message) {
    if (!this.error) return;

    this.error.textContent = message;
    this.error.hidden = false;
  }

  hideError() {
    if (this.error) {
      this.error.hidden = true;
    }
  }

  showToast(message) {
    if (!this.toast) return;

    window.clearTimeout(this.toastTimer);

    this.toast.textContent = message;
    this.toast.classList.add('is-visible');

    this.toastTimer =
      window.setTimeout(() => {
        this.toast.classList.remove('is-visible');
      }, 3600);
  }

  handleGlobalKeydown(event) {
    if (
      this.posterLightbox?.classList.contains('is-open')
    ) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.closePosterLightbox();
        return;
      }

      if (event.key === 'Tab') {
        const focusable = [
          ...this.posterLightbox.querySelectorAll(
            'button:not([disabled])'
          )
        ];

        if (!focusable.length) return;

        event.preventDefault();
        focusable[0].focus();
      }

      return;
    }

    if (
      !this.overlay?.classList.contains('is-open')
    ) {
      return;
    }

    if (event.key === 'Escape') {
      this.closeConfigurator();
      return;
    }

    if (event.key !== 'Tab') return;

    const focusable = [
      ...this.overlay.querySelectorAll(
        'button:not([disabled]), input:not([disabled]), summary, a[href]'
      )
    ];

    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (
      event.shiftKey &&
      document.activeElement === first
    ) {
      event.preventDefault();
      last.focus();

    } else if (
      !event.shiftKey &&
      document.activeElement === last
    ) {
      event.preventDefault();
      first.focus();
    }
  }
}

if (!customElements.get('monteleone-box-grid')) {
  customElements.define(
    'monteleone-box-grid',
    MonteleoneBoxGrid
  );
}
